import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatCHF, currentMonthString, formatMonat } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { PageContainer, PageHeader, MonthPicker } from "../components/PageHeader";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import { Download, AlertCircle, TrendingUp, Wallet, Truck, Receipt, Coins, Upload, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";

function StatCard({ label, value, sublabel, accent, testId, icon: Icon }) {
  return (
    <Card className={`stat-card p-5 border-neutral-200 bg-white`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-[0.1em] text-neutral-500">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-neutral-400" />}
      </div>
      <div className={`mt-3 text-2xl font-semibold nivara-num ${accent || ""}`}>{value}</div>
      {sublabel && <div className="mt-1 text-xs text-neutral-500">{sublabel}</div>}
    </Card>
  );
}

const COLORS = ["#0055FF", "#008A00", "#FFB800", "#E60000", "#525252", "#0040CC", "#7C3AED"];

export default function Dashboard() {
  const [monat, setMonat] = useState(currentMonthString());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [smartUploading, setSmartUploading] = useState(false);
  const [smartResult, setSmartResult] = useState(null);
  const smartFileRef = useRef();
  const navigate = useNavigate();

  const TYPE_LABELS = {
    daily_report: "Tagesrapport",
    qm_plan: "QM-Planung",
    qm_aufwand: "QM-Aufwand (7Days)",
    spedition: "Spedition-Aufwand",
    customer_invoice: "7Days-Gesamtrechnung",
    tt_aufwand: "Tagestouren-Aufwand",
    unknown: "Unbekannt",
  };

  const handleSmartUpload = async (file) => {
    setSmartUploading(true);
    setSmartResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("monat", monat);
    try {
      const r = await api.post("/smart-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSmartResult(r.data);
      const label = TYPE_LABELS[r.data.type_detected] || r.data.type_detected;
      toast.success(`Erkannt: ${label}`);
      // reload dashboard data after a few sec to reflect changes
      setTimeout(() => load(monat), 1500);
    } catch (e) {
      toast.error("Smart-Upload fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setSmartUploading(false);
      if (smartFileRef.current) smartFileRef.current.value = "";
    }
  };

  const load = async (m) => {
    setLoading(true);
    try {
      const r = await api.get(`/dashboard`, { params: { monat: m } });
      setData(r.data);
    } catch (e) {
      toast.error("Fehler beim Laden des Dashboards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(monat);
  }, [monat]);

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/export/pdf/${monat}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NIVARA_${monat}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF Export fehlgeschlagen");
    }
  };

  const downloadExcel = async () => {
    try {
      const r = await api.get(`/export/excel/${monat}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NIVARA_${monat}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Excel Export fehlgeschlagen");
    }
  };

  const bereiche = data ? Object.entries(data.bereiche).map(([name, v]) => ({ name, ...v })) : [];

  return (
    <PageContainer>
      <PageHeader
        title="Dashboard"
        subtitle={`Monatsübersicht · ${formatMonat(monat)}`}
        testId="dashboard-header"
      >
        <MonthPicker value={monat} onChange={setMonat} testId="dashboard-month-picker" />
        <Button
          variant="outline"
          onClick={downloadPdf}
          data-testid="export-pdf-button"
          className="border-neutral-300"
        >
          <Download className="h-4 w-4 mr-2" /> PDF
        </Button>
        <Button
          variant="outline"
          onClick={downloadExcel}
          data-testid="export-excel-button"
          className="border-neutral-300"
        >
          <Download className="h-4 w-4 mr-2" /> Excel
        </Button>
      </PageHeader>

      {/* Smart-Upload Hero Card */}
      <Card className="border-[#0055FF]/20 bg-gradient-to-br from-[#0055FF]/5 to-white p-5 mb-6" data-testid="smart-upload-card">
        <input
          type="file"
          ref={smartFileRef}
          className="hidden"
          accept="image/*,application/pdf,.xlsx,.xls"
          onChange={(e) => e.target.files?.[0] && handleSmartUpload(e.target.files[0])}
          data-testid="smart-upload-input"
        />
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Wand2 className="h-4 w-4 text-[#0055FF]" />
              <span className="text-xs uppercase tracking-[0.15em] text-[#0055FF] font-semibold">KI Smart-Upload</span>
            </div>
            <h2 className="text-lg font-semibold">Ein Dokument hochladen — die KI erkennt automatisch was es ist</h2>
            <p className="text-sm text-neutral-600 mt-1">
              Tagesrapport, QM-Plan, QM-Aufwand, Spedi-Aufwand, TT-Aufwand oder 7Days-Gesamtrechnung — einfach hochladen, die KI routet automatisch zum richtigen Bereich.
            </p>
          </div>
          <Button
            onClick={() => smartFileRef.current?.click()}
            disabled={smartUploading}
            data-testid="smart-upload-button"
            size="lg"
            className="bg-[#0055FF] hover:bg-[#0040CC] text-white shrink-0"
          >
            {smartUploading ? <Sparkles className="h-4 w-4 mr-2 animate-pulse" /> : <Upload className="h-4 w-4 mr-2" />}
            {smartUploading ? "KI analysiert…" : "Datei hochladen"}
          </Button>
        </div>
        {smartResult && (
          <div className="mt-4 pt-4 border-t border-[#0055FF]/15">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500">Erkannter Typ</div>
                <div className="text-sm font-semibold mt-0.5">
                  {TYPE_LABELS[smartResult.type_detected] || smartResult.type_detected}
                  <Badge className="ml-2 bg-neutral-100 text-neutral-700 border-0 text-[10px]">
                    {Math.round((smartResult.confidence || 0) * 100)}%
                  </Badge>
                </div>
                {smartResult.reason && <div className="text-xs text-neutral-500 mt-1">{smartResult.reason}</div>}
                {smartResult.result && (
                  <div className="text-xs text-neutral-700 mt-2 font-mono">
                    {smartResult.result.created != null && `Erstellt: ${smartResult.result.created} · `}
                    {smartResult.result.updated != null && `Aktualisiert: ${smartResult.result.updated} · `}
                    {smartResult.result.skipped != null && `Übersprungen: ${smartResult.result.skipped} · `}
                    {smartResult.result.rows_parsed != null && `Erkannt: ${smartResult.result.rows_parsed} · `}
                    {smartResult.result.invoice_id && `Auto-Abgleich gestartet (Monatsabschluss).`}
                    {smartResult.result.daily_report_id && `Tagesrapport erstellt.`}
                    {smartResult.result.info}
                  </div>
                )}
              </div>
              {smartResult.ui_route && smartResult.ui_route !== "/dashboard" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(smartResult.ui_route)}
                  className="border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF]/10"
                  data-testid="smart-upload-goto-button"
                >
                  Zum Bereich öffnen →
                </Button>
              )}
            </div>
          </div>
        )}
      </Card>

      {loading && !data ? (
        <div className="text-sm text-neutral-500" data-testid="dashboard-loading">
          Lade Kennzahlen…
        </div>
      ) : data ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <StatCard
              label="Gesamtumsatz"
              value={formatCHF(data.gesamt_umsatz)}
              icon={TrendingUp}
              testId="stat-umsatz"
            />
            <StatCard
              label="Fahrerkosten"
              value={formatCHF(data.fahrer_kosten)}
              icon={Wallet}
              testId="stat-fahrer"
            />
            <StatCard
              label="Fahrzeugkosten"
              value={formatCHF(data.fahrzeug_kosten)}
              icon={Truck}
              testId="stat-fahrzeug"
            />
            <StatCard
              label="Sonstige Kosten"
              value={formatCHF(data.sonstige_kosten)}
              icon={Coins}
              testId="stat-sonstige"
            />
            <StatCard
              label="Bruttogewinn"
              value={formatCHF(data.brutto_gewinn)}
              sublabel="Umsatz − Fahrer − Fahrzeug − Sonstige"
              accent={data.brutto_gewinn >= 0 ? "text-[#008A00]" : "text-[#E60000]"}
              icon={Receipt}
              testId="stat-gewinn"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <StatCard
              label="Offene Rapporte"
              value={data.rapporte_offen}
              sublabel={`${data.rapporte_total} insgesamt`}
              testId="stat-rapporte-offen"
            />
            <StatCard
              label="Offene Differenzen"
              value={data.offene_differenzen}
              accent={data.offene_differenzen > 0 ? "text-[#E60000]" : ""}
              testId="stat-differenzen"
            />
            <Card className="p-5 border-neutral-200 bg-white" data-testid="stat-closing">
              <div className="text-xs uppercase tracking-[0.1em] text-neutral-500">
                Monatsabschluss
              </div>
              <div className="mt-3 flex items-center">
                <Badge
                  className={
                    data.abschluss_status === "gesperrt"
                      ? "bg-[#008A00] text-white"
                      : "bg-[#FFB800] text-neutral-900"
                  }
                >
                  {data.abschluss_status === "gesperrt" ? "Abgeschlossen" : "Offen"}
                </Badge>
              </div>
            </Card>
          </div>

          <Card className="p-5 border-neutral-200 bg-white mb-6" data-testid="bereich-chart">
            <h3 className="text-base font-semibold mb-4">Umsatz vs. Fahrerlohn pro Bereich</h3>
            <div className="h-[300px] min-h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bereiche}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#525252" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#525252" />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E5E7EB" }}
                    formatter={(v) => formatCHF(v)}
                  />
                  <Bar dataKey="umsatz" fill="#0055FF" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="fahrer" fill="#FFB800" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="gewinn" fill="#008A00" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-5 border-neutral-200 bg-white" data-testid="gewinn-fahrer-card">
              <h3 className="text-base font-semibold mb-4">Gewinn pro Fahrer</h3>
              {data.gewinn_pro_fahrer.length === 0 ? (
                <div className="text-sm text-neutral-500">Keine Einsätze in diesem Monat.</div>
              ) : (
                <div className="space-y-2">
                  {data.gewinn_pro_fahrer.slice(0, 8).map((f) => (
                    <div
                      key={f.id}
                      className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0"
                    >
                      <div className="text-sm font-medium">{f.name}</div>
                      <div className="text-right">
                        <div className="nivara-num text-sm">{formatCHF(f.umsatz)}</div>
                        <div className="nivara-num text-xs text-[#008A00]">
                          {formatCHF(f.gewinn)} Gewinn
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-5 border-neutral-200 bg-white" data-testid="gewinn-fahrzeug-card">
              <h3 className="text-base font-semibold mb-4">Gewinn pro Fahrzeug</h3>
              {data.gewinn_pro_fahrzeug.length === 0 ? (
                <div className="text-sm text-neutral-500">Keine Einsätze in diesem Monat.</div>
              ) : (
                <div className="space-y-2">
                  {data.gewinn_pro_fahrzeug.slice(0, 8).map((f) => (
                    <div
                      key={f.id}
                      className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0"
                    >
                      <div className="text-sm font-medium font-mono">{f.kennzeichen}</div>
                      <div className="text-right">
                        <div className="nivara-num text-sm">{formatCHF(f.umsatz)}</div>
                        <div
                          className={`nivara-num text-xs ${
                            f.gewinn >= 0 ? "text-[#008A00]" : "text-[#E60000]"
                          }`}
                        >
                          {formatCHF(f.gewinn)} Gewinn
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      ) : (
        <div className="text-sm text-neutral-500 flex items-center gap-2">
          <AlertCircle className="h-4 w-4" /> Keine Daten verfügbar.
        </div>
      )}
    </PageContainer>
  );
}
